/**
 * 🔑 LICENSING SERVICE
 * 
 * Handles all licensing operations including:
 * - Startup license verification (Kill Switch)
 * - License storage and retrieval
 * - Communication with Cloudflare Worker API
 * - Sync engine initialization for ONLINE licenses
 */

import { syncService } from './syncService';
import { getEnabledModules, resolveModuleDisplayName } from '../config/licenseFeatures';
import { getLicensingApiBase } from '../../shared/utils/licensingApiConfig';
import i18n from '../../i18n';

const sm = (key, opts) => i18n.t(`serviceMessages.licensing.${key}`, opts);

const MODULE_CHANGE_NOTICE_KEY = 'qawaem_module_change_notice';

// 🔑 LICENSING WORKER — localhost uses setupProxy
const LICENSING_API_URL = getLicensingApiBase();

class LicensingService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.setupOnlineDetection();
  }

  setupOnlineDetection() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.onConnectionRestored();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  async onConnectionRestored() {
    // Connection restored - No automatic verification needed (Permanent Offline Mode)
    // License expiration is handled by TTL only
    console.log('🌐 Connection restored - Permanent Offline Mode (no re-verification needed)');
  }

  /**
   * 🚨 KILL SWITCH: Verify license on app startup (Online + Offline Mode)
   * This runs every time the app starts to check:
   * 1. If online: Verify with worker (checks if key is deleted/banned)
   * 2. If offline: Fallback to TTL expiration check only
   */
  async verifyStartupLicense() {
    console.log('🔍 Starting license verification (Online Kill Switch + Offline TTL)...');
    
    try {
      const savedLicense = this.getSavedLicense();
      
      if (!savedLicense) {
        console.log('❌ No saved license found');
        return { valid: false, reason: 'NO_LICENSE', message: sm('no_license_found') };
      }

      if (savedLicense.key === 'QAWAEM-CLOUD-DEMO') {
        console.log('✅ Synthetic demo Cloud license (without worker verification)');
        return { valid: true, reason: 'CLOUD_DEMO' };
      }

      if (savedLicense.key === 'QAWAEM-DIRECT') {
        console.log('✅ Qawaem direct access (without license or login)');
        return { valid: true, reason: 'DIRECT_ACCESS' };
      }

      // 🧹 Clear feature service cache to ensure fresh data
      try {
        const featureService = require('./featureService').default;
        featureService.clearCache();
        console.log('🧹 Feature cache cleared');
      } catch (e) {
        console.warn('Could not clear feature cache:', e);
      }

      // 🌐 STEP 1: Try online verification with worker (Kill Switch for deleted/banned keys)
      if (this.isOnline) {
        try {
          console.log('🌐 Online mode detected - verifying with worker...');
          const serverResponse = await this.verifyWithServer(savedLicense.key);
          
          if (serverResponse.success && serverResponse.data.success) {
            // ✅ Worker says key is still valid
            console.log('✅ License verified with worker | Status: VALID');

            const syncResult = this.syncLicenseModulesFromServer(savedLicense, serverResponse.data);
            const activeLicense = syncResult.license || savedLicense;
            
            if (activeLicense.expiresAt) {
              const daysRemaining = Math.ceil((new Date(activeLicense.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
              console.log(`⏰ License expires in ${daysRemaining} days`);
            } else {
              console.log('♾️ License is permanent (no expiration)');
            }
            
            // 🔄 Initialize sync engine for ONLINE licenses
            if (activeLicense.licenseMode === 'ONLINE' && activeLicense.companyId) {
              console.log('🔄 Initializing sync engine for ONLINE license...');
              try {
                await syncService.initialize(activeLicense.key, activeLicense.companyId);
                console.log('✅ Sync engine initialized');
              } catch (syncError) {
                console.warn('⚠️ Sync initialization failed:', syncError);
                // Don't fail license verification if sync fails
              }
            }
            
            return {
              valid: true,
              reason: 'VERIFIED_ONLINE',
              modulesUpdated: syncResult.updated,
            };
            
          } else {
            // ❌ Worker says key is invalid/deleted/banned
            console.log('❌ Worker verification failed:', serverResponse.data.error || 'Key is invalid/deleted/banned');
            this.clearLicense();
            return { 
              valid: false, 
              reason: 'INVALID_KEY', 
              message: serverResponse.data.error || sm('invalid_or_revoked_key')
            };
          }
        } catch (workerError) {
          console.warn('⚠️ Worker verification failed, falling back to offline mode:', workerError.message);
          // Fall through to offline TTL check
        }
      } else {
        console.log('📴 Offline mode - skipping worker verification');
      }

      // 📴 STEP 2: Offline fallback - Check TTL Expiration only
      console.log('⏰ Performing offline TTL validation...');
      const expirationCheck = this.checkLicenseExpiration(savedLicense);
      
      if (!expirationCheck.valid) {
        console.log('❌ License expired (TTL)');
        this.clearLicense();
        return expirationCheck;
      }

      // ✅ License is valid based on TTL (offline mode)
      console.log('✅ License verified successfully (Offline Mode) | Type:', savedLicense.keyType);
      
      if (savedLicense.expiresAt) {
        const daysRemaining = Math.ceil((new Date(savedLicense.expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
        console.log(`⏰ License expires in ${daysRemaining} days`);
      } else {
        console.log('♾️ License is permanent (no expiration)');
      }
      
      return { valid: true, reason: 'VERIFIED_OFFLINE' };
      
    } catch (error) {
      console.error('🚨 License verification error:', error);
      return { 
        valid: false, 
        reason: 'ERROR', 
        message: sm('verification_error')
      };
    }
  }

  /**
   * Human-readable labels for modules in a license pack.
   */
  getModuleDisplayLabels(appType) {
    if (!appType) return [];
    return getEnabledModules({ appType, keyType: appType }).map(
      (key) => resolveModuleDisplayName(key, i18n.t.bind(i18n)),
    );
  }

  /**
   * Queue a startup / in-session toast when Console changed the module pack.
   */
  queueModuleChangeNotice({ previousModules, newModules }) {
    if (typeof window === 'undefined') return;
    const payload = {
      previousModules: previousModules || [],
      newModules: newModules || [],
      at: new Date().toISOString(),
    };
    try {
      sessionStorage.setItem(MODULE_CHANGE_NOTICE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('Could not store module change notice:', e);
    }
      window.dispatchEvent(
        new CustomEvent('qawaem-license-modules-changed', { detail: payload }),
      );
  }

  /**
   * 🔄 Merge appType / keyType from worker into local storage (Console module edits).
   */
  syncLicenseModulesFromServer(savedLicense, serverData) {
    if (!savedLicense || !serverData) return { updated: false, license: savedLicense };

    const appType = serverData.appType || serverData.keyType;
    const keyType = serverData.keyType || serverData.appType;
    if (!appType) return { updated: false, license: savedLicense };

    const previousAppType = savedLicense.appType || savedLicense.keyType || '';
    const unchanged =
      savedLicense.appType === appType &&
      savedLicense.keyType === keyType &&
      (serverData.expiresAt === undefined || savedLicense.expiresAt === serverData.expiresAt);

    if (unchanged) return { updated: false, license: savedLicense };

    const previousModules = this.getModuleDisplayLabels(previousAppType);
    const newModules = this.getModuleDisplayLabels(appType);

    const updated = {
      ...savedLicense,
      appType,
      keyType,
      lastChecked: new Date().toISOString(),
    };

    if (serverData.expiresAt !== undefined) {
      updated.expiresAt = serverData.expiresAt;
    }
    if (serverData.licenseMode) {
      updated.licenseMode = serverData.licenseMode;
    }
    if (serverData.companyId) {
      updated.companyId = serverData.companyId;
    }

    this.saveLicense(updated);

    try {
      const featureService = require('./featureService').default;
      featureService.clearCache();
    } catch (e) {
      console.warn('Could not clear feature cache after license sync:', e);
    }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('qawaem-license-synced', { detail: { appType, keyType } }));
        this.queueModuleChangeNotice({ previousModules, newModules });
      }

    console.log('🔄 License modules synced from server:', appType);
    return {
      updated: true,
      license: updated,
      previousModules,
      newModules,
    };
  }

  /**
   * @param {Object} license - The license object from localStorage
   * @returns {Object} - Validation result
   */
  checkLicenseExpiration(license) {
    // If no expiration date, license is permanent
    if (!license.expiresAt) {
      return { valid: true };
    }

    const now = new Date();
    const expirationDate = new Date(license.expiresAt);

    if (now > expirationDate) {
      return {
        valid: false,
        reason: 'EXPIRED',
        message: sm('expired')
      };
    }

    return { valid: true };
  }

  /**
   * 🌐 API CALL: Check license with Cloudflare Worker
   * Uses /check-license endpoint for already activated licenses
   */
  async verifyWithServer(key) {
    try {
      const response = await fetch(`${LICENSING_API_URL}/check-license?key=${encodeURIComponent(key)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      return {
        success: response.status === 200 && data.success,
        status: response.status,
        data: data
      };
      
    } catch (error) {
      console.error('License API error:', error);
      return {
        success: false,
        status: 500,
        data: { error: 'Network error', code: 'NETWORK_ERROR' }
      };
    }
  }

  /**
   * 📄 Get saved license from localStorage
   */
  getSavedLicense() {
    try {
      const saved = localStorage.getItem('qawaem_license');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error reading saved license:', error);
      localStorage.removeItem('qawaem_license');
      return null;
    }
  }

  /**
   * 💾 Save license to localStorage
   */
  saveLicense(licenseData) {
    try {
      localStorage.setItem('qawaem_license', JSON.stringify(licenseData));
      return true;
    } catch (error) {
      console.error('Error saving license:', error);
      return false;
    }
  }

  /**
   * 🗑️ Clear saved license
   */
  clearLicense() {
    localStorage.removeItem('qawaem_license');
    console.log('🗑️ License cleared');
  }

  /**
   * 🚨 Handle invalid license (Kill Switch activation)
   */
  handleInvalidLicense(reason) {
    console.log('🚨 KILL SWITCH ACTIVATED:', reason);
    
    // Clear any saved license
    this.clearLicense();
    
    // Force redirect to setup page
    if (window.location.pathname !== '/setup') {
      console.log('🔄 Redirecting to setup page...');
      window.location.href = '/setup';
    }
  }

  /**
   * 📊 Get license status for display
   */
  getLicenseStatus() {
    const license = this.getSavedLicense();
    
    if (!license) {
      return {
        isValid: false,
        status: 'NO_LICENSE',
        message: sm('no_active_license')
      };
    }

    return {
      isValid: true,
      status: 'ACTIVE',
      key: license.key,
      activatedAt: license.activatedAt,
      lastChecked: license.lastChecked,
      message: sm('active_license')
    };
  }

  /**
   * 🔄 Force license re-verification
   */
  async forceLicenseCheck() {
    console.log('🔄 Force license verification requested...');
    return await this.verifyStartupLicense();
  }

  /**
   * 🔑 Get license type (tier) for feature gating
   * @returns {string|null} - License type (FACTURATION, MANAGEMENT, INVENTAIRE, ENTERPRISE)
   */
  getLicenseType() {
    const license = this.getSavedLicense();
    if (!license) {
      return null;
    }
    return license.keyType || license.appType || 'FACTURATION'; // Fallback to FACTURATION if not set
  }

  /**
   * 🔑 Get full license object
   * @returns {Object|null} - Full license object or null
   */
  getLicense() {
    return this.getSavedLicense();
  }

  /**
   * 📇 Push company contact info to Console Clients (after ERP setup step 3).
   */
  async syncClientProfileToConsole({ email, phone, clientName } = {}) {
    const license = this.getSavedLicense();
    if (!license?.key || license.key === 'QAWAEM-CLOUD-DEMO') {
      return { success: false, skipped: true, reason: 'NO_LICENSE' };
    }
    if (!navigator.onLine) {
      return { success: false, reason: 'OFFLINE' };
    }

    const params = new URLSearchParams({ key: license.key });
    const em = email && String(email).trim();
    const ph = phone && String(phone).trim();
    const nm = clientName && String(clientName).trim();
    if (em) params.set('clientEmail', em);
    if (ph) params.set('clientPhone', ph);
    if (nm) params.set('clientName', nm);

    if (!params.has('clientEmail') && !params.has('clientPhone') && !params.has('clientName')) {
      return { success: false, reason: 'NO_CONTACT' };
    }

    try {
      const response = await fetch(`${LICENSING_API_URL}/sync-client-profile?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      return {
        success: response.ok && data.success,
        status: response.status,
        data,
      };
    } catch (error) {
      console.error('Console client profile sync error:', error);
      return {
        success: false,
        reason: 'NETWORK_ERROR',
        message: error.message,
      };
    }
  }
}

// Export singleton instance
const licensingService = new LicensingService();

export default licensingService;

// Named exports for specific functions
export const verifyStartupLicense = () => licensingService.verifyStartupLicense();
export const getLicenseStatus = () => licensingService.getLicenseStatus();
export const getLicenseType = () => licensingService.getLicenseType();
export const getLicense = () => licensingService.getLicense();
export const clearLicense = () => licensingService.clearLicense();
export const forceLicenseCheck = () => licensingService.forceLicenseCheck();
